import type { WritableComputedRef } from 'vue';
import type { AnyItem, CompositePrimaryKey, RelationOperationToOne, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { FormBlockContext } from '#layers/forms/types/form-block';
import { toValue } from 'vue';
import { randomUuid } from '#layers/base/public';
import { getItemPrimaryKey, getRelationConstraint, getRelationPrimitiveFields, isConnectToOne, isSameKey, nestedToOneConnect } from '#layers/data-model/public';
import {
	createdItemKey,
	fromReferencedItemKey,
	verifyItemKeys,
} from '#layers/forms/composables/relation-field-state-primitives';

export function createRelationFieldStateToOneInCreateHandler(field: ResolvedMonospaceSingleRelationField, fieldValue: WritableComputedRef<RelationOperationToOne | undefined, RelationOperationToOne | undefined>, formBlockContext: FormBlockContext | null) {
	function handleCreate(payload: AnyItem) {
		fieldValue.value = { _create: { data: { ...payload, [createdItemKey]: randomUuid() } } };
		if (!getRelationConstraint(field).thisSideConstrained) return;

		const { fields: pkFields } = getRelationPrimitiveFields(field);
		const formState = formBlockContext?.state.value;

		if (!formState) return;

		for (const field of pkFields) {
			formState[field.apiName] = null;
		}
	}

	function handleConnect(key: CompositePrimaryKey) {
		fieldValue.value = nestedToOneConnect({ key, mode: 'create' });

		const primitiveValues = fromReferencedItemKey(key, field);
		const formState = formBlockContext?.state.value;

		if (!primitiveValues || !formState) return;

		for (const [fieldKey, value] of Object.entries(primitiveValues)) {
			formState[fieldKey] = value;
		}
	}

	function handleDisconnect(key: CompositePrimaryKey) {
		fieldValue.value = undefined;

		// make sure the primitive fields on this item are set to `null`
		const primitiveValues = fromReferencedItemKey(key, field);
		const formState = formBlockContext?.state.value;

		if (!primitiveValues || !formState) return;

		for (const fieldKey of Object.keys(primitiveValues)) {
			formState[fieldKey] = null;
		}
	}

	function handleUpdate(originalKey: CompositePrimaryKey | null, payload: AnyItem) {
		const key = originalKey ?? getItemPrimaryKey(payload, toValue(field).oppositeCollection!);
		const isNewlyConnectedItem = isConnectToOne(fieldValue.value!) && isSameKey(fieldValue.value!._connect.key, key);
		verifyItemKeys(key, payload, toValue(field).oppositeCollection!);

		if (!isNewlyConnectedItem) {
			fieldValue.value = { _create: { data: { ...payload, [createdItemKey]: randomUuid() } } };
		}
		else {
			throw new Error('Update after connect is currently not supposed.');
		}
	}

	return {
		handleConnect,
		handleDisconnect,
		handleCreate,
		handleUpdate,
	};
}
